import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHandymanAuth } from '../context/HandymanAuthContext';
import axios from 'axios';
import { API } from '../config/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { 
  Briefcase, Calendar, Clock, MapPin, DollarSign, User,
  Check, X, Loader2, LogOut, Phone, Mail, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const statusColors = {
  new: 'bg-blue-100 text-blue-800',
  assigned: 'bg-yellow-100 text-yellow-800',
  accepted: 'bg-green-100 text-green-800',
  declined: 'bg-red-100 text-red-800',
  completed: 'bg-purple-100 text-purple-800'
};

export const HandymanDashboard = () => {
  const navigate = useNavigate();
  const { handyman, isAuthenticated, loading: authLoading, logout } = useHandymanAuth();
  
  const [enquiries, setEnquiries] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [actionType, setActionType] = useState(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/handyman/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  const fetchData = useCallback(async () => {
    try {
      const [enquiriesRes, scheduleRes] = await Promise.all([
        axios.get(`${API}/handyman/enquiries`),
        axios.get(`${API}/handyman/schedule`)
      ]);
      setEnquiries(enquiriesRes.data);
      setSchedule(scheduleRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated, fetchData]);

  const handleAction = async () => {
    if (!selectedEnquiry || !actionType) return;
    
    setActionLoading(selectedEnquiry.id);
    try {
      await axios.post(`${API}/handyman/enquiries/${selectedEnquiry.id}/${actionType}`);
      toast.success(
        actionType === 'accept' ? 'Job accepted!' : 
        actionType === 'decline' ? 'Job declined' : 
        'Job marked as completed!'
      );
      fetchData();
    } catch (error) {
      toast.error('Failed to perform action');
    } finally {
      setActionLoading(null);
      setSelectedEnquiry(null);
      setActionType(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const newEnquiries = enquiries.filter(e => e.status === 'new' || e.status === 'assigned');
  const activeJobs = enquiries.filter(e => e.status === 'accepted');
  const completedJobs = enquiries.filter(e => e.status === 'completed');

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-muted/30 py-8" data-testid="handyman-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold font-['Outfit']">
              Welcome, {handyman?.name?.split(' ')[0]}!
            </h1>
            <p className="text-muted-foreground">Manage your enquiries and schedule</p>
          </div>
          <Button variant="outline" onClick={handleLogout} data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>

        <Tabs defaultValue="enquiries">
          <TabsList className="mb-6">
            <TabsTrigger value="enquiries" data-testid="tab-enquiries">
              Enquiries ({newEnquiries.length})
            </TabsTrigger>
            <TabsTrigger value="active" data-testid="tab-active">
              Active Jobs ({activeJobs.length})
            </TabsTrigger>
            <TabsTrigger value="schedule" data-testid="tab-schedule">
              Schedule
            </TabsTrigger>
            <TabsTrigger value="profile" data-testid="tab-profile">
              Profile
            </TabsTrigger>
          </TabsList>

          {/* Enquiries Tab */}
          <TabsContent value="enquiries">
            {newEnquiries.length === 0 ? (
              <Card className="border-border">
                <CardContent className="py-16 text-center">
                  <Briefcase className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <h3 className="text-xl font-semibold mb-2 font-['Outfit']">No new enquiries</h3>
                  <p className="text-muted-foreground">New job requests will appear here</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {newEnquiries.map((enquiry) => (
                  <Card key={enquiry.id} className="border-border" data-testid={`enquiry-${enquiry.id}`}>
                    <CardContent className="p-6">
                      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <h3 className="font-semibold text-lg font-['Outfit']">{enquiry.service_name}</h3>
                            <Badge className={statusColors[enquiry.status]}>{enquiry.status}</Badge>
                          </div>
                          
                          <p className="text-muted-foreground mb-4">{enquiry.description}</p>
                          
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <User className="w-4 h-4" />
                              <span>{enquiry.customer_name}</span>
                            </div>
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Mail className="w-4 h-4" />
                              <span>{enquiry.customer_email}</span>
                            </div>
                            {enquiry.customer_phone && (
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <Phone className="w-4 h-4" />
                                <span>{enquiry.customer_phone}</span>
                              </div>
                            )}
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <MapPin className="w-4 h-4" />
                              <span>{enquiry.address}</span>
                            </div>
                            {enquiry.booking_date && (
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <Calendar className="w-4 h-4" />
                                <span>{format(parseISO(enquiry.booking_date), 'MMM d, yyyy')} at {enquiry.booking_time}</span>
                              </div>
                            )}
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <DollarSign className="w-4 h-4" />
                              <span className="font-semibold text-foreground">£{enquiry.total_cost.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex gap-2">
                          <Button
                            className="btn-accent"
                            onClick={() => { setSelectedEnquiry(enquiry); setActionType('accept'); }}
                            disabled={actionLoading === enquiry.id}
                            data-testid={`accept-${enquiry.id}`}
                          >
                            <Check className="w-4 h-4 mr-1" /> Accept
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => { setSelectedEnquiry(enquiry); setActionType('decline'); }}
                            disabled={actionLoading === enquiry.id}
                            data-testid={`decline-${enquiry.id}`}
                          >
                            <X className="w-4 h-4 mr-1" /> Decline
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Active Jobs Tab */}
          <TabsContent value="active">
            {activeJobs.length === 0 ? (
              <Card className="border-border">
                <CardContent className="py-16 text-center">
                  <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <h3 className="text-xl font-semibold mb-2 font-['Outfit']">No active jobs</h3>
                  <p className="text-muted-foreground">Accepted jobs will appear here</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {activeJobs.map((job) => (
                  <Card key={job.id} className="border-border" data-testid={`job-${job.id}`}>
                    <CardContent className="p-6">
                      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <h3 className="font-semibold text-lg font-['Outfit']">{job.service_name}</h3>
                            <Badge className={statusColors[job.status]}>{job.status}</Badge>
                          </div>
                          
                          <p className="text-muted-foreground mb-4">{job.description}</p>
                          
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <User className="w-4 h-4" />
                              <span>{job.customer_name}</span>
                            </div>
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Phone className="w-4 h-4" />
                              <span>{job.customer_phone || job.customer_email}</span>
                            </div>
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <MapPin className="w-4 h-4" />
                              <span>{job.address}</span>
                            </div>
                            {job.booking_date && (
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <Calendar className="w-4 h-4" />
                                <span>{format(parseISO(job.booking_date), 'MMM d, yyyy')} at {job.booking_time}</span>
                              </div>
                            )}
                            <div className="flex items-center gap-2">
                              <DollarSign className="w-4 h-4 text-muted-foreground" />
                              <span className="font-semibold">£{job.total_cost.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                        
                        <Button
                          onClick={() => { setSelectedEnquiry(job); setActionType('complete'); }}
                          disabled={actionLoading === job.id}
                          data-testid={`complete-${job.id}`}
                        >
                          <Check className="w-4 h-4 mr-1" /> Mark Complete
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Schedule Tab */}
          <TabsContent value="schedule">
            {schedule.length === 0 ? (
              <Card className="border-border">
                <CardContent className="py-16 text-center">
                  <Calendar className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <h3 className="text-xl font-semibold mb-2 font-['Outfit']">No scheduled jobs</h3>
                  <p className="text-muted-foreground">Your upcoming appointments will appear here</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {schedule.map((item) => (
                  <Card key={item.id} className="border-border">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-xl bg-primary/10 flex flex-col items-center justify-center">
                          <span className="text-lg font-bold text-primary">
                            {format(parseISO(item.booking_date), 'd')}
                          </span>
                          <span className="text-xs text-primary uppercase">
                            {format(parseISO(item.booking_date), 'MMM')}
                          </span>
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold font-['Outfit']">{item.service_name}</h3>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {item.booking_time}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {item.address}
                            </span>
                          </div>
                        </div>
                        <Badge className={statusColors[item.status]}>{item.status}</Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="font-['Outfit']">Your Profile</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm text-muted-foreground">Name</label>
                    <p className="font-medium">{handyman?.name}</p>
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground">Email</label>
                    <p className="font-medium">{handyman?.email}</p>
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground">Phone</label>
                    <p className="font-medium">{handyman?.phone}</p>
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground">Experience</label>
                    <p className="font-medium">{handyman?.experience_years} years</p>
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground">Coverage Areas</label>
                    <p className="font-medium">{handyman?.coverage_areas || 'Not specified'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground">Status</label>
                    <Badge className="bg-green-100 text-green-800">{handyman?.status}</Badge>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm text-muted-foreground">Services</label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {handyman?.services?.map((service) => (
                      <Badge key={service} variant="secondary">{service}</Badge>
                    ))}
                  </div>
                </div>
                
                {handyman?.certifications && (
                  <div>
                    <label className="text-sm text-muted-foreground">Certifications</label>
                    <p className="font-medium">{handyman.certifications}</p>
                  </div>
                )}
                
                {handyman?.bio && (
                  <div>
                    <label className="text-sm text-muted-foreground">Bio</label>
                    <p className="font-medium">{handyman.bio}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Action Confirmation Dialog */}
      <AlertDialog open={!!selectedEnquiry} onOpenChange={() => setSelectedEnquiry(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {actionType === 'accept' && 'Accept this job?'}
              {actionType === 'decline' && 'Decline this job?'}
              {actionType === 'complete' && 'Mark job as complete?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {actionType === 'accept' && 'You will be assigned to this job and the customer will be notified.'}
              {actionType === 'decline' && 'This job will be returned to the pool for other handymen.'}
              {actionType === 'complete' && 'This will mark the job as finished.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleAction}
              className={actionType === 'decline' ? 'bg-destructive hover:bg-destructive/90' : 'btn-accent'}
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
